import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, TABLE_PREFIX } from "./supabaseClient";

// Namespacing: every physical table name is prefixed (e.g. "store_") so this
// app can share one Supabase project with other demos. Logical names stay
// unprefixed everywhere else (toRow/fromRow, filters), so only the .from()
// boundary changes. Falls back to no prefix when TABLE_PREFIX is unset.
const T = (name) => (TABLE_PREFIX || "") + name;

// ============================================================
// This module replaces the old localStorage usePersistedReducer.
// It exposes hooks that return [state, dispatch] with the SAME action
// shapes the components already use (ADD / UPDATE / DELETE / RECEIVE /
// SELL / RETURN), but each action reads/writes Supabase.
//
// Field mapping note: the app uses camelCase keys (storePrice, lowAlert,
// eventId, customerEmail...). The DB uses snake_case. We translate in the
// to/from row helpers below so the components never need to change.
// ============================================================

// ---- column translation helpers ----
const camelToSnakeMaps = {
  products: { storePrice: "store_price", lowAlert: "low_alert", sectionId: "section_id" },
  materials: { lowAlert: "low_alert", lotPrice: "lot_price", lotUnits: "lot_units", taxRate: "tax_rate", categoryId: "category_id" },
  sections: {},
  mat_categories: {},
  settings: { vendorPrefix: "vendor_prefix", taxRate: "tax_rate", skuNameLen: "sku_name_len", skuMaxLen: "sku_max_len", skuNoDashes: "sku_no_dashes", matTaxOn: "mat_tax_on", matTaxRate: "mat_tax_rate", collectiveCut: "collective_cut", dashboardLayout: "dashboard_layout" },
  deals: { productSku: "product_sku", sectionName: "section_name", minQty: "min_qty", bundlePrice: "bundle_price", extraUnit: "extra_unit" },
  events: { taxRate: "tax_rate" },
  venue_inventory: { eventId: "event_id" },
  received: { eventId: "event_id" },
  orders: { eventId: "event_id", customerEmail: "customer_email", customerPhone: "customer_phone" },
  expenses: {},
  customers: {},
};

function toRow(table, obj) {
  const map = camelToSnakeMaps[table] || {};
  // Keys that exist on the app object but not as DB columns — drop them.
  const dropKeys = { orders: ["event"] };
  const drops = dropKeys[table] || [];
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === "id") continue; // DB generates ids
    if (drops.includes(k)) continue;
    out[map[k] || k] = v;
  }
  return out;
}

function fromRow(table, row) {
  const map = camelToSnakeMaps[table] || {};
  const inv = Object.fromEntries(Object.entries(map).map(([c, s]) => [s, c]));
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    out[inv[k] || k] = v;
  }
  return out;
}

// ---------------------------------------------------------------
// useTable: a list-backed table (mirrors listReducer: ADD/UPDATE/DELETE)
// `filter` optionally scopes rows (e.g. { channel: 'store' }); inserts
// automatically include the filter values so new rows belong to the scope.
// ---------------------------------------------------------------
export function useTable(table, filter = {}, ready = true) {
  const [rows, setRows] = useState([]);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const load = useCallback(async () => {
    if (!ready) return;
    let q = supabase.from(T(table)).select("*");
    for (const [k, v] of Object.entries(filterRef.current)) q = q.eq(k, v);
    const { data, error } = await q.order("created_at", { ascending: true });
    if (!error && data) setRows(data.map(r => fromRow(table, r)));
  }, [table, ready]);

  useEffect(() => { load(); }, [load]);

  const dispatch = useCallback(async (action) => {
    if (action.type === "ADD") {
      const row = { ...toRow(table, action.item), ...filterRef.current };
      const { data, error } = await supabase.from(T(table)).insert(row).select().single();
      if (error) console.error(`[${table}] ADD failed:`, error.message || error);
      if (!error && data) { const mapped = fromRow(table, data); setRows(prev => [...prev, mapped]); return mapped; }
    } else if (action.type === "UPDATE") {
      const changes = toRow(table, action.changes);
      const { data, error } = await supabase.from(T(table)).update(changes).eq("id", action.id).select().single();
      if (error) console.error(`[${table}] UPDATE failed:`, error.message || error);
      if (!error && data) { const mapped = fromRow(table, data); setRows(prev => prev.map(r => r.id === action.id ? mapped : r)); return mapped; }
    } else if (action.type === "DELETE") {
      const { error } = await supabase.from(T(table)).delete().eq("id", action.id);
      if (error) console.error(`[${table}] DELETE failed:`, error.message || error);
      if (!error) setRows(prev => prev.filter(r => r.id !== action.id));
    }
    return null;
  }, [table]);

  return [rows, dispatch, load];
}

// ---------------------------------------------------------------
// useVenueInventory: mirrors storeInvReducer (RECEIVE/SELL/RETURN/DELETE)
// against the venue_inventory table, scoped by venue ('store'|'event').
// RECEIVE merges by name+size within the same event scope.
// ---------------------------------------------------------------
export function useVenueInventory(venue, ready = true) {
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    if (!ready) return;
    const { data, error } = await supabase.from(T("venue_inventory")).select("*").eq("venue", venue).order("created_at", { ascending: true });
    if (!error && data) setRows(data.map(r => fromRow("venue_inventory", r)));
  }, [venue, ready]);

  useEffect(() => { load(); }, [load]);

  const dispatch = useCallback(async (action) => {
    if (action.type === "RECEIVE") {
      const { product, qty, eventId } = action;
      const evId = eventId || null;
      // find existing same-sku, same-event row
      const { data: existingRows } = await supabase.from(T("venue_inventory")).select("*")
        .eq("venue", venue).eq("name", product.name).eq("size", product.size || "");
      const existing = (existingRows || []).find(r => (r.event_id || null) === evId);
      if (existing) {
        const newQty = Number(existing.qty) + Number(qty);
        const { data } = await supabase.from(T("venue_inventory")).update({ qty: newQty, price: Number(product.price) || existing.price }).eq("id", existing.id).select().single();
        if (data) setRows(prev => prev.map(r => r.id === existing.id ? fromRow("venue_inventory", data) : r));
      } else {
        const row = { venue, event_id: evId, name: product.name, size: product.size || "", flavor: product.flavor || "", price: Number(product.price) || 0, qty: Number(qty), section: product.section || "", sku: product.sku || "", taxable: product.taxable !== false };
        const { data } = await supabase.from(T("venue_inventory")).insert(row).select().single();
        if (data) setRows(prev => [...prev, fromRow("venue_inventory", data)]);
      }
    } else if (action.type === "SELL") {
      const target = rows.find(r => r.id === action.id);
      if (!target) return;
      const newQty = Math.max(0, Number(target.qty) - Number(action.qty));
      const { data } = await supabase.from(T("venue_inventory")).update({ qty: newQty }).eq("id", action.id).select().single();
      if (data) setRows(prev => prev.map(r => r.id === action.id ? fromRow("venue_inventory", data) : r));
    } else if (action.type === "RETURN") {
      const target = rows.find(r => r.id === action.id);
      if (!target) return;
      const newQty = Math.max(0, Number(target.qty) - Number(action.qty));
      if (newQty <= 0) {
        const { error } = await supabase.from(T("venue_inventory")).delete().eq("id", action.id);
        if (!error) setRows(prev => prev.filter(r => r.id !== action.id));
      } else {
        const { data } = await supabase.from(T("venue_inventory")).update({ qty: newQty }).eq("id", action.id).select().single();
        if (data) setRows(prev => prev.map(r => r.id === action.id ? fromRow("venue_inventory", data) : r));
      }
    } else if (action.type === "DELETE") {
      const { error } = await supabase.from(T("venue_inventory")).delete().eq("id", action.id);
      if (!error) setRows(prev => prev.filter(r => r.id !== action.id));
    }
  }, [venue, rows]);

  return [rows, dispatch, load];
}

// ---------------------------------------------------------------
// useReceived: mirrors listReducer but against the `received` table,
// scoped by venue. (ADD/DELETE used by the app.)
// ---------------------------------------------------------------
export function useReceived(venue, ready = true) {
  return useTable("received", { venue }, ready);
}
